import { ReactNode, useState } from "react";
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Link,
  MenuItem,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useQuery } from "@tanstack/react-query";
import SideDrawer from "components/SideDrawer";
import useDebounce from "hooks/useDebounce";
import useResettableState from "hooks/useResettableState";
import {
  BreakdownItem,
  CategoryDetailQuery,
  CategoryEntry,
  FinanceQuery,
  FlightEntryBooking,
  formatNaira,
  getApiError,
  getFinanceCategoryDetail,
} from "services/finance";
import { Booking } from "services/manualBooking";
import BookingDetailsPanel from "../ManualBookings/BookingDetailsPanel";

const LIMIT = 20;
const COLUMN_COUNT = 6;

const PRODUCT_LABELS = {
  flight: "Flights",
  ride: "Rides",
  general: "General",
} as const;

const headCellSx = {
  bgcolor: "#F2F4F7",
  color: "#475467",
  fontSize: 12,
  fontWeight: 600,
  borderBottom: "none",
  whiteSpace: "nowrap",
};

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

// Ledger dates are UTC timestamps; admins read them in Lagos time.
const formatLagosDate = (value: string) => {
  const d = new Date(value);
  return isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "Africa/Lagos",
      });
};

// The first segment's endpoints stop a round trip reading as "LOS → LOS".
const toPanelBooking = (flight: FlightEntryBooking): Booking => ({
  id: flight.bookingDocId,
  booking_id: flight.bookingId,
  airline: flight.airline || "",
  from: flight.segments?.[0]?.from || "",
  to: flight.segments?.[0]?.to || "",
  contact: flight.contactEmail || "",
  email: flight.contactEmail,
  amount: flight.ticketPrice || 0,
  status: flight.status,
  date: flight.bookedAt || "",
  pnr: flight.pnr,
  segments: flight.segments?.map(({ from, to, departureTime }) => ({
    from,
    to,
    departureTime,
  })),
});

const TwoLine = ({ primary, secondary }: { primary: ReactNode; secondary?: ReactNode }) => (
  <>
    <Typography fontSize={14} color="#101828">
      {primary || "—"}
    </Typography>
    {secondary && (
      <Typography fontSize={12} color="#667085">
        {secondary}
      </Typography>
    )}
  </>
);

const StatusChip = ({ label }: { label: string }) => (
  <Chip
    size="small"
    label={capitalize(label)}
    sx={{
      borderRadius: "6px",
      fontSize: 12,
      fontWeight: 600,
      bgcolor: "#F2F4F7",
      color: "#475467",
    }}
  />
);

// Every row type fills the same columns, because one line can mix flights,
// rides and manual entries.
const getRowCells = (entry: CategoryEntry) => {
  const { booking } = entry;

  if (booking?.type === "flight") {
    return {
      reference: <TwoLine primary={booking.pnr || entry.reference} secondary={booking.bookingId} />,
      details: <TwoLine primary={booking.route} secondary={booking.airline} />,
      party: <TwoLine primary={booking.traveller} />,
      status: booking.bookingMode ? <StatusChip label={booking.bookingMode} /> : "—",
    };
  }

  if (booking?.type === "ride") {
    const payoutStatus = entry.payoutStatus || booking.vendorPayout?.status;
    return {
      reference: <TwoLine primary={booking.rideCode} />,
      details: (
        <TwoLine primary={`${booking.pickupLocation || "—"} → ${booking.dropOffLocation || "—"}`} />
      ),
      party: <TwoLine primary={booking.customer} secondary={booking.driver?.name} />,
      status: payoutStatus ? <StatusChip label={payoutStatus} /> : "—",
    };
  }

  return {
    reference: <TwoLine primary={entry.reference} />,
    details: <TwoLine primary={entry.description} secondary={entry.notes} />,
    party: (
      <TwoLine
        primary={entry.createdBy}
        secondary={
          entry.receipt && (
            <Link href={entry.receipt} target="_blank" rel="noopener noreferrer">
              Receipt
            </Link>
          )
        }
      />
    ),
    status: <StatusChip label={entry.source} />,
  };
};

const CategoryDetailDrawer = ({
  line,
  query,
  onClose,
}: {
  line: BreakdownItem;
  query: FinanceQuery;
  onClose: () => void;
}) => {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"date" | "amount">("date");
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const debouncedSearch = useDebounce(search.trim(), 400);

  // Back to the first page whenever the filters change.
  const [page, setPage] = useResettableState(
    1,
    [sort, debouncedSearch, query.period, query.start, query.end, query.product].join("|"),
  );

  const params: CategoryDetailQuery = {
    ...query,
    search: debouncedSearch,
    sort,
    page,
    limit: LIMIT,
  };

  const detailQuery = useQuery({
    queryKey: ["finance", "categoryDetail", line.category, params],
    queryFn: () => getFinanceCategoryDetail(line.category, params),
    placeholderData: (prev) => prev,
    retry: false,
  });

  const detail = detailQuery.data?.data;
  const items = detail?.items || [];
  const totalPages = detail?.pagination?.pages ?? 0;
  const summary = detail?.summary;
  const productSplit = (Object.keys(PRODUCT_LABELS) as (keyof typeof PRODUCT_LABELS)[]).filter(
    (key) => summary?.byProduct?.[key] !== undefined,
  );

  return (
    <SideDrawer open onClose={onClose} title={detail?.label || line.label} width={960}>
      <Box bgcolor="#F9FAFB" border="1px solid #E4E7EC" borderRadius="8px" p="16px" mb="16px">
        {detailQuery.isLoading ? (
          <Skeleton height={56} />
        ) : (
          <>
            <Typography fontSize={24} fontWeight={700} color="#101828">
              {formatNaira(summary?.total)}
            </Typography>
            <Typography fontSize={13} color="#667085">
              {summary?.entries ?? 0} entries · {summary?.bookings ?? 0} bookings
            </Typography>
            {productSplit.length > 0 && (
              <Box display="flex" flexWrap="wrap" gap="16px" mt="8px">
                {productSplit.map((key) => (
                  <Typography key={key} fontSize={13} color="#475467">
                    {PRODUCT_LABELS[key]}: <strong>{formatNaira(summary?.byProduct[key])}</strong>
                  </Typography>
                ))}
              </Box>
            )}
          </>
        )}
      </Box>

      <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap="12px" mb="16px">
        <TextField
          size="small"
          placeholder="Search PNR, booking ID, ride code, FIN ref..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#98A2B3" }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          value={sort}
          onChange={(e) => setSort(e.target.value as "date" | "amount")}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="date">Newest first</MenuItem>
          <MenuItem value="amount">Largest first</MenuItem>
        </TextField>
      </Box>

      {detailQuery.isError ? (
        <Box py="24px" textAlign="center">
          <Typography fontSize={13} color="#D92D20" mb="8px">
            {getApiError(detailQuery.error, "Could not load these entries.")}
          </Typography>
          <Button size="small" onClick={() => detailQuery.refetch()}>
            Retry
          </Button>
        </Box>
      ) : (
        <TableContainer
          sx={{
            overflowX: "auto",
            opacity: detailQuery.isFetching && !detailQuery.isLoading ? 0.6 : 1,
          }}
        >
          <Table sx={{ minWidth: 820 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headCellSx, borderRadius: "8px 0 0 8px" }}>Reference</TableCell>
                <TableCell sx={headCellSx}>Details</TableCell>
                <TableCell sx={headCellSx}>Customer / Added by</TableCell>
                <TableCell sx={headCellSx}>Status</TableCell>
                <TableCell sx={headCellSx}>Date</TableCell>
                <TableCell sx={{ ...headCellSx, borderRadius: "0 8px 8px 0" }} align="right">
                  Amount
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detailQuery.isLoading ? (
                [0, 1, 2, 3, 4].map((i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={COLUMN_COUNT}>
                      <Skeleton height={28} />
                    </TableCell>
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT}>
                    <Typography textAlign="center" color="#98A2B3" fontSize={14} py="24px">
                      No entries found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((entry) => {
                  const flight = entry.booking?.type === "flight" ? entry.booking : null;
                  const cells = getRowCells(entry);
                  const openFlight = flight
                    ? () => setSelectedBooking(toPanelBooking(flight))
                    : undefined;
                  return (
                    <TableRow
                      key={entry.id}
                      hover={Boolean(flight)}
                      onClick={openFlight}
                      onKeyDown={
                        openFlight
                          ? (e) => {
                              if (e.key === "Enter") openFlight();
                            }
                          : undefined
                      }
                      tabIndex={flight ? 0 : undefined}
                      sx={{ cursor: flight ? "pointer" : "default" }}
                    >
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{cells.reference}</TableCell>
                      <TableCell>{cells.details}</TableCell>
                      <TableCell>{cells.party}</TableCell>
                      <TableCell>{cells.status}</TableCell>
                      <TableCell sx={{ fontSize: 14, color: "#475467", whiteSpace: "nowrap" }}>
                        {formatLagosDate(entry.date)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: 14,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          color: entry.amount < 0 ? "#D92D20" : "#101828",
                        }}
                      >
                        {formatNaira(entry.amount)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt="20px">
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            size="small"
          />
        </Box>
      )}

      <BookingDetailsPanel
        open={Boolean(selectedBooking)}
        onClose={() => setSelectedBooking(null)}
        booking={selectedBooking}
      />
    </SideDrawer>
  );
};

export default CategoryDetailDrawer;
